import angular from 'angular'

import { wrap, identity } from 'lodash-es'

import setCss from './set-css'
import envRaw from './env-raw'
import MapSimple from './map-simple'
import extractRawElm from './extract-raw-elm'

angular
  .module('ggs.utils')
  .factory('AdvancedHeightCache', AdvancedHeightCacheProvider)

const CLONE_CSS = {
  top: -10000,
  left: -10000,
  position: 'absolute',
  width: null,
  height: null,
  margin: null,
  marginTop: null,
  marginRight: null,
  marginBottom: null,
  marginLeft: null,
  padding: null,
  paddingTop: null,
  paddingRight: null,
  paddingBottom: null,
  paddingLeft: null,
  overflow: null,
  overflowX: null,
  overflowY: null
}

function AdvancedHeightCacheProvider(
  $log,
  domh,
  DataChannel,
  IdleChannel,
  addIdPrefix) {

  let nextId = 0
  const holder = domh.createHolder(addIdPrefix('advancedHeightCache'))
  const name2meta = MapSimple()

  return function AdvancedHeightCache(name, $elm, toBaseWidth = identity) {
    const id = nextId++
    const sign = `advancedHeightCache[name=${name}][id=${id}]`

    const elm = extractRawElm($elm)
    const meta = name2meta.has(name)
      ? name2meta.get(name)
      : name2meta.set(name, DataChannel(`advancedHeightCache::${name}`))

    const gears = {
      id,
      elm,
      meta,
      name,
      sign,
      twin: null,
      last: null,
      cache: MapSimple().setMaxPairs(200),
      clearCount: 0,
      testersRead: null,
      testersWrite: null,
      toBaseWidth
    }

    gears.calc = wrap(gears, calc)
    gears.fill = wrap(gears, fill)
    gears.clear = wrap(gears, clear)
    gears.destroy = wrap(gears, destroy)

    gears.preEntry = wrap(gears, preEntry)
    gears.onEntry = wrap(gears, onEntry)

    gears.meta.emitter.pre(gears.preEntry)
    gears.meta.emitter.on(gears.onEntry)

    return gears
  }

  // onEntry

  function preEntry(gears, baseWidth) {
    if (!gears.cache.has(baseWidth)) {
      if (envRaw.showsAdvancedHeightCacheDebug) $log.force.debug(`${gears.sign}: ${baseWidth} width will be applied!`)
      setTwinWidth(gears, baseWidth)
    }
  }

  function onEntry(gears, baseWidth) {
    if (!gears.cache.has(baseWidth)) {
      if (envRaw.showsAdvancedHeightCacheDebug) $log.force.debug(`${gears.sign}: ${baseWidth} width will be cached!`)
      gears.cache.set(baseWidth, getTwinHeight(gears, baseWidth))
    }
  }

  // clear

  function clear(gears) {
    if (gears.twin) {
      domh.remove.notStrict(gears.twin)
      gears.twin = null
    }

    gears.last = null
    gears.cache = MapSimple().setMaxPairs(200)
    gears.clearCount++
    return gears
  }

  // calc

  function calc(gears, width) {
    const baseWidth = gears.toBaseWidth(width)
    const index = gears.cache.getIndex(baseWidth)
    if (index !== -1) {
      return gears.cache.getByIndex(index)
    } else {
      if (envRaw.showsAdvancedHeightCacheDebug) $log.force.debug(`${gears.sign}: ${baseWidth} width not found in cache!`)
      const height = getTwinHeight(gears, baseWidth)
      gears.cache.set(baseWidth, height)
      gears.meta.add(baseWidth)
      return height
    }
  }

  function setTwinWidth(gears, width) {
    if (!gears.twin) {
      gears.twin = cloneElm(gears.elm)
    }

    if (gears.last !== width) {
      gears.last = width
      setWidth(gears.twin, width)
    }
  }

  function getTwinHeight(gears, width) {
    if (!gears.twin) {
      gears.twin = cloneElm(gears.elm)
    }

    setTwinWidth(gears, width)
    return getHeight(gears.twin)
  }

  // fill

  function fill(gears) {
    gears.testersWrite = IdleChannel('advancedHeightCache::testersWrite')
    return gears.testersWrite.queue(() => {
      const local = gears.clearCount
      const widths = gears.meta.entries.concat()
      const testers = renderTesters(gears.elm, widths)
      gears.testersRead = IdleChannel('advancedHeightCache::testersRead')
      return gears.testersRead.queue(() => {
        if (local !== gears.clearCount) {
          if (envRaw.showsAdvancedHeightCacheDebug) $log.force.debug(`${gears.sign}: filling process has been interrupted!`)
          return
        }

        const heights = measureTesters(testers)
        for (let index = 0, length = heights.length; index !== length; index++) {
          const width = widths[index]
          const height = heights[index]
          gears.cache.replace(width, height)
        }
      }).finally(() => {
        destroyTesters(testers)
      })
    }).finally(() => {
      gears.testersRead = null
      gears.testersWrite = null
    })
  }

  function renderTesters(elm, widths) {
    const testers = []
    for (let index = 0, length = widths.length; index !== length; index++) {
      const width = widths[index]
      const tester = renderTester(elm, width)
      testers.push(tester)
    }
    return testers
  }

  function measureTesters(testers) {
    const heights = []
    for (let index = 0, length = testers.length; index !== length; index++) {
      const tester = testers[index]
      const height = measureTester(tester)
      heights.push(height)
    }
    return heights
  }

  function destroyTesters(testers) {
    for (let index = 0, length = testers.length; index !== length; index++) {
      const tester = testers[index]
      destroyTester(tester)
    }
  }

  function renderTester(elm, width) {
    const clone = cloneElm(elm)
    setWidth(clone, width)
    return clone
  }

  function measureTester(tester) {
    return getHeight(tester)
  }

  function destroyTester(tester) {
    domh.remove.notStrict(tester)
  }

  // destroy

  function destroy(gears) {
    gears.meta.emitter.off(gears.preEntry)
    gears.meta.emitter.off(gears.onEntry)
    if (gears.twin) domh.remove.notStrict(gears.twin)
    if (gears.testersRead) gears.testersRead.cancelAll()
    if (gears.testersWrite) gears.testersWrite.cancelAll()
  }

  // helpers

  function cloneElm(elm) {
    const clone = elm.cloneNode(true)
    setCss(clone, CLONE_CSS)
    clone.setAttribute('aria-hidden', 'true')
    holder.appendChild(clone)
    return clone
  }

  function getHeight(elm) {
    return Math.round(elm.offsetHeight)
  }

  function setWidth(elm, width) {
    setCss(elm, 'width', width)
    setCss(elm, 'height', 'auto')
    return elm
  }
}
